/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useContext, useState } from 'react';
import IEnergyManagementProps, {
  EUtilityConfig,
  ECartAt
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import AllUtilitiesDetailWidget from '../../AllUtilitiesDetailWidget/AllUtilitiesDetailWidget';
import {
  excludeCurrentMonth,
  formattedDate,
  getEndDateForUtility,
  getFirstDayofSelectedMonth
} from '../../../shared/Utils/Utils';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import AllUtilitiesSavingsByMeterComponent from '../../AllUtilitiesSavingsByMeterWidget/AllUtilitiesSavingsByMeterComponent';
import { getConnctorConfig } from '../../../shared/Utils/DataConnectorUtils';
import { ApiContext } from '../../../../context/ApiContext';
import moment from 'moment';
import NoData from '../../../shared/ErrorComponents/NoData';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { EmaasSubscriptionType } from '../../../shared/Enums';

const AllUtilitiesDetailsComponent = (props: IEnergyManagementProps) => {
  const { bearerToken, requestContext } = props;
  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime =
    requestContext.context.subscriptionType ===
    EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
      ? getEndDateForUtility(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        )
      : excludeCurrentMonth(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        );
  const endTimeNewFormat = formattedDate(endTime);
  const apiContext = useContext(ApiContext);
  const configName =
    apiContext.apiCallAt === ECartAt.portfolioLabel
      ? EUtilityConfig.allUtilityPortfolioLabel
      : EUtilityConfig.allUtilitySiteLabel;
  const connectorConfig = getConnctorConfig(
    configName,
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const isDateRangeValid = () => {
    const sDate = moment(
      getFirstDayofSelectedMonth(requestContext.context.startTime!),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      excludeCurrentMonth(
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <div className="all-utilities-details">
      <AllUtilitiesDetailWidget dataConnector={dataConnector} />
      <AllUtilitiesSavingsByMeterComponent dataConnector={dataConnector} />
    </div>
  );
};
export default AllUtilitiesDetailsComponent;
